import { Component } from '@theme/component';
import { openAliaPopup, urlDiscountCode } from '@theme/alia-popup';

/**
 * Opens the Alia sign-up popup, either on demand (a trigger inside the component
 * uses `on:click="/open"`) or automatically on page load when the component
 * carries `data-auto-open`.
 *
 * The mechanics of talking to the Alia SDK live in `@theme/alia-popup`, shared with
 * `sl-deals-banner.js`.
 *
 * Auto-open runs once per browser session (sessionStorage guard): opening via
 * the SDK can bypass the display targeting configured in Alia, so without the
 * guard the popup would re-open when a customer returns to the landing page
 * right after signing up.
 *
 * When the visitor arrives with a discount code in the URL (Alia redirects
 * back to the landing page with the code after sign-up), the sign-up CTA is
 * swapped for the regular add-to-cart fallback rendered next to it, and
 * auto-open is skipped entirely.
 *
 * @typedef {{ signupButton?: Element, discountFallback?: Element }} Refs
 * @extends {Component<Refs>}
 */
class SignupPopupComponent extends Component {
  /** @type {() => void} */
  #cancelFallback = () => {};

  connectedCallback() {
    super.connectedCallback();

    if (this.#hasUrlDiscount()) {
      this.refs.signupButton?.setAttribute('hidden', '');
      this.refs.discountFallback?.removeAttribute('hidden');
      return;
    }

    if (!this.hasAttribute('data-auto-open') || Shopify.designMode) return;

    const sessionKey = `signup-popup-shown-${this.dataset.campaignId || 'default'}`;
    try {
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, '1');
    } catch {
      // Storage unavailable (privacy mode) — fall through and open anyway.
    }
    this.open();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cancelFallback();
  }

  open() {
    this.#cancelFallback = openAliaPopup(this.dataset.campaignId);
  }

  /**
   * Whether the visitor carries a discount code in the URL — the same
   * `?discount=` signal apply-discount-cards.js uses to rewrite prices.
   * @returns {boolean}
   */
  #hasUrlDiscount() {
    return Boolean(urlDiscountCode());
  }
}

if (!customElements.get('signup-popup-component')) {
  customElements.define('signup-popup-component', SignupPopupComponent);
}
